import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { useNotify } from '../../../components/notifications';
import { fmtLocalDateTime } from '../../../utils/datetime';
import { icmTruncationNote } from '../icmPaging';
import { fetchLtCoaTracking, downloadCoaFile, downloadLabelReport, type LtCoaTrackingRow } from './icmTrackingApi';
import '../../backlog/backlog.css';
import '../alerts/icmAlerts.css';
import './icmTracking.css';

/**
 * Inventory Management → Tracking → LT# COA Tracking.
 *
 * Every lot that has a vendor lot number (VendorLT) — the certificate-of-
 * analysis trail per lot. Ported from ALPACA `Distribution_ListView_LT_Tracking`
 * (criteria `Not IsNullOrEmpty([VendorLT])`), which is read-only and adds two
 * inline row actions: "COA" (opens the certificate PDF, hidden when none is
 * attached — legacy `Grid_HtmlCommandCellPrepared`) and "Label" (the same
 * "Label Report" PDF — QR + Item Name + LT No + Dist Date + Exp Date +
 * Storage — as Item Transaction History's toolbar action, for this one lot;
 * legacy `PreviewQRLabel_Execute`).
 *
 * Division-scoped here even though legacy leaves this one view unfiltered —
 * see `AI_ICM_TrackingLtCoa_List_SP`'s header for the fix rationale.
 *
 * Legacy sets `ShowSelectionColumn=False` on this view, but a row-selection
 * checkbox column is shown here on user request (2026-09-22) for consistency
 * with the other Tracking grids — separate from `busyOid`, which tracks the
 * in-flight COA/Label row action, not the checkbox selection.
 */
export default function LtCoaTrackingPage() {
  const notify = useNotify();

  const [rows, setRows] = useState<LtCoaTrackingRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [busyOid, setBusyOid] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const page = await fetchLtCoaTracking();
      setRows(page.rows);
      setTotal(page.total);
      setSelected(new Set());
    } catch {
      setError('Failed to load LT# COA tracking.');
      setRows([]); setTotal(0);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      (r.lt ?? '').toLowerCase().includes(t) ||
      (r.vendorLt ?? '').toLowerCase().includes(t) ||
      (r.itemName ?? '').toLowerCase().includes(t) ||
      (r.itemCode ?? '').toLowerCase().includes(t) ||
      (r.vendorName ?? '').toLowerCase().includes(t)
    );
  }, [rows, search]);

  async function handleCoa(r: LtCoaTrackingRow) {
    setBusyOid(r.oid);
    try {
      await downloadCoaFile(r.oid, r.lt);
    } catch {
      notify.error('Failed to download the COA file.');
    } finally {
      setBusyOid(null);
    }
  }

  async function handleLabel(r: LtCoaTrackingRow) {
    if (!r.lt) { notify.warning('This lot has no LT# to print a label for.'); return; }
    setBusyOid(r.oid);
    try {
      await downloadLabelReport([r.oid]);
    } catch {
      notify.error('Failed to generate the label report.');
    } finally {
      setBusyOid(null);
    }
  }

  /* Column set + ORDER from the live GoLive grid (owner screenshot 2026-10-05):
     LT# · VendorLT · COA · QRLabel · Item · Vendor · Catalog# · Storage ·
     Department · RequestedDate · DistributedDate · ExpiryDate · Status.
     The two command columns sit THIRD and FOURTH, right after VendorLT — they
     were last here, which is the far end of a 13-column grid you have to scroll
     to reach. */
  const cols: AppColumn<LtCoaTrackingRow>[] = [
    { field: 'lt',                header: 'LT#',         width: 110, text: r => r.lt ?? '—', render: r => r.lt ?? '—' },
    { field: 'vendorLt',          header: 'VendorLT',    width: 130, text: r => r.vendorLt ?? '—', render: r => r.vendorLt ?? '—' },
    /* COA / QRLabel — the SAME "Attachment" link Item Initialization already
       renders for these two legacy command columns (ItemInitListPage, owner
       2026-09-30), reused rather than restyled per form (standing rule 8).

       ⚠️ `bl-link` IS LOAD-BEARING, not decoration. The app-wide black-cell rule
       (theme/blackText.css) forces `var(--ai-ink)` on everything inside a `td`,
       so without it these render as plain dark text where GoLive paints blue
       links. That rule carries an explicit opt-out list — .bl-link /
       .mlv-link-blue / .dv-att-link — which is the sanctioned way to say "this
       one IS a link"; `adg-celllink` then supplies #1565c0, the app's link blue
       and the closest match to GoLive's.

       ⚠️ COA is BLANK, not "—", when nothing is attached. That is what GoLive
       shows on THIS view (the owner's screenshot: blank on the first eight lots,
       "Attachment" from 2600679 down) — unlike Item Initialization, where the
       word appears on every row because the column is painted as a caption. */
    {
      field: 'coa', header: 'COA', width: 110, align: 'center',
      text: r => r.hasCoaAttachment ? 'Attachment' : '',
      render: r => r.hasCoaAttachment ? (
        <button
          type="button"
          className="adg-celllink bl-link"
          disabled={busyOid === r.oid}
          onClick={e => { e.stopPropagation(); void handleCoa(r); }}
          title="Preview the certificate of analysis"
        >
          Attachment
        </button>
      ) : null,
    },
    /* QRLabel — legacy's caption for this column (VendorReagentCertificateViewController
       sets Columns["PreviewQRLabel"].Caption = "QRLabel"); AI said "Label".
       It is NOT a stored attachment despite reading "Attachment": legacy renders
       the LTBarcode report for the row's LT and opens the PDF. */
    {
      field: 'label', header: 'QRLabel', width: 110, align: 'center',
      text: () => 'Attachment',
      render: r => (
        <button
          type="button"
          className="adg-celllink bl-link"
          disabled={busyOid === r.oid || !r.lt}
          onClick={e => { e.stopPropagation(); void handleLabel(r); }}
          title="Print the label report (QR + item + LT# + dates + storage) for this lot"
        >
          Attachment
        </button>
      ),
    },
    { field: 'itemName',          header: 'Item',        width: 200, text: r => r.itemName ?? '—', render: r => r.itemName ?? '—' },
    { field: 'vendorName',        header: 'Vendor',      width: 140, text: r => r.vendorName ?? '—', render: r => r.vendorName ?? '—' },
    { field: 'catalog',           header: 'Catalog#',    width: 110, text: r => r.catalog ?? '—', render: r => r.catalog ?? '—' },
    { field: 'storageName',       header: 'Storage',     width: 120, text: r => r.storageName ?? '—', render: r => r.storageName ?? '—' },
    { field: 'departmentName',    header: 'Department',  width: 130, text: r => r.departmentName ?? '—', render: r => r.departmentName ?? '—' },
    { field: 'requestedDate',     header: 'RequestedDate',  width: 150, type: 'date', text: r => fmtLocalDateTime(r.requestedDate) ?? '—', render: r => fmtLocalDateTime(r.requestedDate) ?? '—' },
    { field: 'distributionDate',  header: 'DistributedDate',  width: 150, type: 'date', text: r => fmtLocalDateTime(r.distributionDate) ?? '—', render: r => fmtLocalDateTime(r.distributionDate) ?? '—' },
    { field: 'expiryDate',        header: 'ExpiryDate',      width: 130, type: 'date', text: r => fmtLocalDateTime(r.expiryDate) ?? '—', render: r => fmtLocalDateTime(r.expiryDate) ?? '—' },
    { field: 'statusLabel',       header: 'Status',      width: 140, type: 'status', text: r => r.statusLabel ?? '—', render: r => r.statusLabel ?? '—' },
  ];

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Inventory Management</span><i className="ti ti-chevron-right" />
            <span>Tracking</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">LT# COA Tracking</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="icm-lt-coa-tracking" loadMs={loadMs} loading={loading} />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            onRefresh={load}
            refreshing={loading}
            exportData={{ filename: 'lt-coa-tracking', ...gridExportMatrix(filtered, cols.filter(c => c.field !== 'coa' && c.field !== 'label')) }}
          />
        </div>
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search LT#, vendor LT, item, vendor…"
              data-unsaved-ignore
            />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          <span className="ica-note">{total.toLocaleString()} lot(s)</span>
          {/* The ceiling must never be silent: before 2026-10-05 this header
              showed the true total while the grid below paged a truncated
              2,000, with no way to tell. */}
          {icmTruncationNote(rows.length, total) && (
            <span className="ica-note ica-note-warn">{icmTruncationNote(rows.length, total)}</span>
          )}
        </div>
        <div className="bl-card bl-card-flex">
          {error ? (
            <div className="bl-err"><i className="ti ti-alert-circle" /> {error}</div>
          ) : (
            <AppDataGrid<LtCoaTrackingRow>
              rows={filtered}
              columns={cols}
              getRowId={r => r.oid}
              loading={loading}
              loadMs={loadMs}
              defaultPageSize={20}
              selectable
              selectedIds={selected}
              onSelectionChange={setSelected}
              emptyText="No lots with a vendor lot number found."
            />
          )}
        </div>
      </div>
    </div>
  );
}
